

import { useState } from "react"
import { useNavigate } from "react-router-dom"
import { ChevronRight, CreditCard, Truck, ShoppingBag, ClipboardCheck, Loader2, PenLine } from "lucide-react"
import { useCart } from "../../context/cart-context"
import { useOrder } from "../../context/order-context"
import Breadcrumbs from "../../components/goods/bread-crumb"

export default function CheckoutPage() {
  const navigate = useNavigate()
  const { items, totalAmount, clearCart } = useCart()
  const { createOrder } = useOrder()

  const [formData, setFormData] = useState({
    firstName: "",
    lastName: "",
    email: "",
    phone: "",
    address: "",
    city: "",
    state: "",
    zipCode: "",
    country: "United States",
    paymentMethod: "cash",
  })

  const [errors, setErrors] = useState({})
  const [step, setStep] = useState(1)
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [editSection, setEditSection] = useState(null)

  // Calculate shipping cost
  const shippingCost = totalAmount > 100 ? 0 : 10

  // Calculate tax (assuming 8%)
  const taxRate = 0.08
  const taxAmount = totalAmount * taxRate

  // Calculate order total
  const orderTotal = totalAmount + shippingCost + taxAmount

  const handleChange = (e) => {
    const { name, value } = e.target
    setFormData((prev) => ({ ...prev, [name]: value }))

    // Clear error when field is edited
    if (errors[name]) {
      setErrors((prev) => ({ ...prev, [name]: "" }))
    }
  }

  const validateStep1 = () => {
    const newErrors = {}

    if (!formData.firstName.trim()) newErrors.firstName = "First name is required"
    if (!formData.lastName.trim()) newErrors.lastName = "Last name is required"
    if (!formData.email.trim()) {
      newErrors.email = "Email is required"
    } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
      newErrors.email = "Email is invalid"
    }
    if (!formData.phone.trim()) newErrors.phone = "Phone number is required"

    setErrors(newErrors)
    return Object.keys(newErrors).length === 0
  }

  const validateStep2 = () => {
    const newErrors = {}

    if (!formData.address.trim()) newErrors.address = "Address is required"
    if (!formData.city.trim()) newErrors.city = "City is required"
    if (!formData.state.trim()) newErrors.state = "State is required"
    if (!formData.zipCode.trim()) newErrors.zipCode = "ZIP code is required"

    setErrors(newErrors)
    return Object.keys(newErrors).length === 0
  }

  const handleNextStep = () => {
    if (step === 1 && validateStep1()) {
      setStep(2)
      window.scrollTo(0, 0)
    } else if (step === 2 && validateStep2()) {
      setStep(3)
      window.scrollTo(0, 0)
    } else if (step === 3) {
      setStep(4)
      window.scrollTo(0, 0)
    }
  }

  const handlePrevStep = () => {
    setStep((prev) => Math.max(1, prev - 1))
    window.scrollTo(0, 0)
  }

  const handleEditSection = (section) => {
    if (section === "contact") {
      setStep(1)
    } else if (section === "shipping") {
      setStep(2)
    } else if (section === "payment") {
      setStep(3)
    }
    setEditSection(section)
    window.scrollTo(0, 0)
  }

  const handleSubmit = (e) => {
    e.preventDefault()
    setIsSubmitting(true)

    // Create order object
    const order = {
      customer: {
        firstName: formData.firstName,
        lastName: formData.lastName,
        email: formData.email,
        phone: formData.phone,
      },
      shipping: {
        address: formData.address,
        city: formData.city,
        state: formData.state,
        zipCode: formData.zipCode,
        country: formData.country,
      },
      payment: {
        method: formData.paymentMethod,
      },
      items: items.map((item) => ({
        id: item.id,
        name: item.name,
        price: item.price,
        quantity: item.quantity,
        total: item.price * item.quantity,
      })),
      totals: {
        subtotal: totalAmount,
        shipping: shippingCost,
        tax: taxAmount,
        total: orderTotal,
      },
    }

    // Log order data to console for future endpoint integration

    // Simulate API call with a delay
    setTimeout(() => {
      // Create order and get order ID
      const orderId = createOrder(order)

      // Clear cart
      clearCart()

      // Navigate to order confirmation page
      navigate(`/goods/order-confirmation/${orderId}`)
    }, 1500)
  }

  if (items.length === 0) {
    navigate("/goods/cart")
    return null
  }

  return (
    <div className="container mx-auto px-4 py-8">
      <Breadcrumbs items={[{ label: "Cart", href: "/goods/cart" }, { label: "Checkout" }]} />

      <h1 className="text-2xl font-bold mb-6">Checkout</h1>

      {/* Checkout Steps */}
      <div className="mb-8">
        <div className="flex items-center">
          <div
            className={`flex items-center justify-center w-8 h-8 rounded-full ${step >= 1 ? "bg-[#ff9906] text-white" : "bg-gray-200 text-gray-600"}`}
          >
            1
          </div>
          <div className={`flex-1 h-1 mx-2 ${step >= 2 ? "bg-[#ff9906]" : "bg-gray-200"}`}></div>
          <div
            className={`flex items-center justify-center w-8 h-8 rounded-full ${step >= 2 ? "bg-[#ff9906] text-white" : "bg-gray-200 text-gray-600"}`}
          >
            2
          </div>
          <div className={`flex-1 h-1 mx-2 ${step >= 3 ? "bg-[#ff9906]" : "bg-gray-200"}`}></div>
          <div
            className={`flex items-center justify-center w-8 h-8 rounded-full ${step >= 3 ? "bg-[#ff9906] text-white" : "bg-gray-200 text-gray-600"}`}
          >
            3
          </div>
          <div className={`flex-1 h-1 mx-2 ${step >= 4 ? "bg-[#ff9906]" : "bg-gray-200"}`}></div>
          <div
            className={`flex items-center justify-center w-8 h-8 rounded-full ${step >= 4 ? "bg-[#ff9906] text-white" : "bg-gray-200 text-gray-600"}`}
          >
            4
          </div>
        </div>
        <div className="flex justify-between mt-2 text-sm">
          <div className="text-center w-8">Contact</div>
          <div className="text-center w-8">Shipping</div>
          <div className="text-center w-8">Payment</div>
          <div className="text-center w-8">Review</div>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
        {/* Checkout Form */}
        <div className="lg:col-span-2">
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
            <form onSubmit={handleSubmit}>
              {/* Step 1: Contact Information */}
              {step === 1 && (
                <div>
                  <h2 className="text-lg font-bold mb-4 flex items-center">
                    <ShoppingBag className="mr-2 h-5 w-5 text-[#ff9906]" />
                    Contact Information
                  </h2>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label htmlFor="firstName" className="block text-sm font-medium text-gray-700 mb-1">
                        First Name *
                      </label>
                      <input
                        type="text"
                        id="firstName"
                        name="firstName"
                        value={formData.firstName}
                        onChange={handleChange}
                        className={`w-full p-2 border rounded-md ${errors.firstName ? "border-red-500" : "border-gray-300"}`}
                      />
                      {errors.firstName && <p className="text-red-500 text-xs mt-1">{errors.firstName}</p>}
                    </div>

                    <div>
                      <label htmlFor="lastName" className="block text-sm font-medium text-gray-700 mb-1">
                        Last Name *
                      </label>
                      <input
                        type="text"
                        id="lastName"
                        name="lastName"
                        value={formData.lastName}
                        onChange={handleChange}
                        className={`w-full p-2 border rounded-md ${errors.lastName ? "border-red-500" : "border-gray-300"}`}
                      />
                      {errors.lastName && <p className="text-red-500 text-xs mt-1">{errors.lastName}</p>}
                    </div>

                    <div>
                      <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-1">
                        Email Address *
                      </label>
                      <input
                        type="email"
                        id="email"
                        name="email"
                        value={formData.email}
                        onChange={handleChange}
                        className={`w-full p-2 border rounded-md ${errors.email ? "border-red-500" : "border-gray-300"}`}
                      />
                      {errors.email && <p className="text-red-500 text-xs mt-1">{errors.email}</p>}
                    </div>

                    <div>
                      <label htmlFor="phone" className="block text-sm font-medium text-gray-700 mb-1">
                        Phone Number *
                      </label>
                      <input
                        type="tel"
                        id="phone"
                        name="phone"
                        value={formData.phone}
                        onChange={handleChange}
                        className={`w-full p-2 border rounded-md ${errors.phone ? "border-red-500" : "border-gray-300"}`}
                      />
                      {errors.phone && <p className="text-red-500 text-xs mt-1">{errors.phone}</p>}
                    </div>
                  </div>
                </div>
              )}

              {/* Step 2: Shipping Information */}
              {step === 2 && (
                <div>
                  <h2 className="text-lg font-bold mb-4 flex items-center">
                    <Truck className="mr-2 h-5 w-5 text-[#ff9906]" />
                    Shipping Information
                  </h2>

                  <div className="grid grid-cols-1 gap-4">
                    <div>
                      <label htmlFor="address" className="block text-sm font-medium text-gray-700 mb-1">
                        Address *
                      </label>
                      <input
                        type="text"
                        id="address"
                        name="address"
                        value={formData.address}
                        onChange={handleChange}
                        className={`w-full p-2 border rounded-md ${errors.address ? "border-red-500" : "border-gray-300"}`}
                      />
                      {errors.address && <p className="text-red-500 text-xs mt-1">{errors.address}</p>}
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <label htmlFor="city" className="block text-sm font-medium text-gray-700 mb-1">
                          City *
                        </label>
                        <input
                          type="text"
                          id="city"
                          name="city"
                          value={formData.city}
                          onChange={handleChange}
                          className={`w-full p-2 border rounded-md ${errors.city ? "border-red-500" : "border-gray-300"}`}
                        />
                        {errors.city && <p className="text-red-500 text-xs mt-1">{errors.city}</p>}
                      </div>

                      <div>
                        <label htmlFor="state" className="block text-sm font-medium text-gray-700 mb-1">
                          State/Province *
                        </label>
                        <input
                          type="text"
                          id="state"
                          name="state"
                          value={formData.state}
                          onChange={handleChange}
                          className={`w-full p-2 border rounded-md ${errors.state ? "border-red-500" : "border-gray-300"}`}
                        />
                        {errors.state && <p className="text-red-500 text-xs mt-1">{errors.state}</p>}
                      </div>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <label htmlFor="zipCode" className="block text-sm font-medium text-gray-700 mb-1">
                          ZIP/Postal Code *
                        </label>
                        <input
                          type="text"
                          id="zipCode"
                          name="zipCode"
                          value={formData.zipCode}
                          onChange={handleChange}
                          className={`w-full p-2 border rounded-md ${errors.zipCode ? "border-red-500" : "border-gray-300"}`}
                        />
                        {errors.zipCode && <p className="text-red-500 text-xs mt-1">{errors.zipCode}</p>}
                      </div>

                      <div>
                        <label htmlFor="country" className="block text-sm font-medium text-gray-700 mb-1">
                          Country *
                        </label>
                        <select
                          id="country"
                          name="country"
                          value={formData.country}
                          onChange={handleChange}
                          className="w-full p-2 border border-gray-300 rounded-md"
                        >
                          <option value="United States">United States</option>
                          <option value="Canada">Canada</option>
                          <option value="United Kingdom">United Kingdom</option>
                          <option value="Australia">Australia</option>
                        </select>
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {/* Step 3: Payment Information */}
              {step === 3 && (
                <div>
                  <h2 className="text-lg font-bold mb-4 flex items-center">
                    <CreditCard className="mr-2 h-5 w-5 text-[#ff9906]" />
                    Payment Method
                  </h2>

                  <div className="space-y-4">
                    <div className="border border-gray-200 rounded-md p-4">
                      <label className="flex items-center">
                        <input
                          type="radio"
                          name="paymentMethod"
                          value="cash"
                          checked={formData.paymentMethod === "cash"}
                          onChange={handleChange}
                          className="h-4 w-4 text-[#ff9906]"
                        />
                        <span className="ml-2 text-gray-700">Cash on Delivery</span>
                      </label>
                      <p className="text-sm text-gray-500 mt-2 ml-6">Pay with cash when your order is delivered.</p>
                    </div>

                    <div className="border border-gray-200 rounded-md p-4 opacity-50">
                      <label className="flex items-center">
                        <input
                          type="radio"
                          name="paymentMethod"
                          value="card"
                          disabled
                          className="h-4 w-4 text-gray-400"
                        />
                        <span className="ml-2 text-gray-500">Credit/Debit Card (Coming Soon)</span>
                      </label>
                    </div>

                    <div className="border border-gray-200 rounded-md p-4 opacity-50">
                      <label className="flex items-center">
                        <input
                          type="radio"
                          name="paymentMethod"
                          value="paypal"
                          disabled
                          className="h-4 w-4 text-gray-400"
                        />
                        <span className="ml-2 text-gray-500">PayPal (Coming Soon)</span>
                      </label>
                    </div>
                  </div>
                </div>
              )}

              {/* Step 4: Review Order */}
              {step === 4 && (
                <div>
                  <h2 className="text-lg font-bold mb-4 flex items-center">
                    <ClipboardCheck className="mr-2 h-5 w-5 text-[#ff9906]" />
                    Review Your Order
                  </h2>

                  <div className="space-y-6">
                    {/* Contact Information Review */}
                    <div className="border border-gray-200 rounded-md p-4">
                      <div className="flex justify-between items-center mb-2">
                        <h3 className="font-medium text-gray-800">Contact Information</h3>
                        <button
                          type="button"
                          onClick={() => handleEditSection("contact")}
                          className="text-[#ff9906] hover:text-[#e68a00] text-sm flex items-center"
                        >
                          <PenLine size={14} className="mr-1" />
                          Edit
                        </button>
                      </div>
                      <div className="text-sm text-gray-600">
                        <p>
                          {formData.firstName} {formData.lastName}
                        </p>
                        <p>{formData.email}</p>
                        <p>{formData.phone}</p>
                      </div>
                    </div>

                    {/* Shipping Information Review */}
                    <div className="border border-gray-200 rounded-md p-4">
                      <div className="flex justify-between items-center mb-2">
                        <h3 className="font-medium text-gray-800">Shipping Address</h3>
                        <button
                          type="button"
                          onClick={() => handleEditSection("shipping")}
                          className="text-[#ff9906] hover:text-[#e68a00] text-sm flex items-center"
                        >
                          <PenLine size={14} className="mr-1" />
                          Edit
                        </button>
                      </div>
                      <div className="text-sm text-gray-600">
                        <p>{formData.address}</p>
                        <p>
                          {formData.city}, {formData.state} {formData.zipCode}
                        </p>
                        <p>{formData.country}</p>
                      </div>
                    </div>

                    {/* Payment Method Review */}
                    <div className="border border-gray-200 rounded-md p-4">
                      <div className="flex justify-between items-center mb-2">
                        <h3 className="font-medium text-gray-800">Payment Method</h3>
                        <button
                          type="button"
                          onClick={() => handleEditSection("payment")}
                          className="text-[#ff9906] hover:text-[#e68a00] text-sm flex items-center"
                        >
                          <PenLine size={14} className="mr-1" />
                          Edit
                        </button>
                      </div>
                      <div className="text-sm text-gray-600">
                        <p>{formData.paymentMethod === "cash" ? "Cash on Delivery" : formData.paymentMethod}</p>
                      </div>
                    </div>

                    {/* Order Items Review */}
                    <div className="border border-gray-200 rounded-md p-4">
                      <h3 className="font-medium text-gray-800 mb-2">Order Items</h3>
                      <div className="space-y-3">
                        {items.map((item) => (
                          <div
                            key={item.id}
                            className="flex justify-between items-center py-2 border-b border-gray-100 last:border-0"
                          >
                            <div className="flex items-center">
                              <div className="w-12 h-12 flex-shrink-0 mr-3">
                                <img
                                  src={item.image || "/placeholder.svg"}
                                  alt={item.name}
                                  className="w-full h-full object-contain"
                                />
                              </div>
                              <div>
                                <p className="text-sm font-medium text-gray-800">{item.name}</p>
                                <p className="text-xs text-gray-500">Qty: {item.quantity}</p>
                              </div>
                            </div>
                            <p className="text-sm font-medium">${(item.price * item.quantity).toFixed(2)}</p>
                          </div>
                        ))}
                      </div>
                    </div>

                    {/* Order Summary Review */}
                    <div className="border border-gray-200 rounded-md p-4">
                      <h3 className="font-medium text-gray-800 mb-2">Order Summary</h3>
                      <div className="space-y-2">
                        <div className="flex justify-between text-sm">
                          <span className="text-gray-600">Subtotal</span>
                          <span>${totalAmount.toFixed(2)}</span>
                        </div>
                        <div className="flex justify-between text-sm">
                          <span className="text-gray-600">Shipping</span>
                          <span>{shippingCost === 0 ? "Free" : `$${shippingCost.toFixed(2)}`}</span>
                        </div>
                        <div className="flex justify-between text-sm">
                          <span className="text-gray-600">Tax (8%)</span>
                          <span>${taxAmount.toFixed(2)}</span>
                        </div>
                        <div className="border-t border-gray-200 pt-2 mt-2">
                          <div className="flex justify-between font-bold">
                            <span>Total</span>
                            <span className="text-[#ff9906]">${orderTotal.toFixed(2)}</span>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              )}

              <div className="mt-8 flex justify-between">
                {step > 1 ? (
                  <button
                    type="button"
                    onClick={handlePrevStep}
                    className="px-4 py-2 text-gray-600 hover:text-gray-800"
                  >
                    Back
                  </button>
                ) : (
                  <div></div>
                )}

                {step < 4 ? (
                  <button
                    type="button"
                    onClick={handleNextStep}
                    className="bg-[#ff9906] hover:bg-[#e68a00] text-white px-6 py-2 rounded-md font-medium transition-colors flex items-center"
                  >
                    Continue
                    <ChevronRight className="ml-1 h-4 w-4" />
                  </button>
                ) : (
                  <button
                    type="submit"
                    disabled={isSubmitting}
                    className="bg-[#ff9906] hover:bg-[#e68a00] text-white px-6 py-2 rounded-md font-medium transition-colors flex items-center disabled:opacity-70 disabled:cursor-not-allowed"
                  >
                    {isSubmitting ? (
                      <>
                        <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                        Processing...
                      </>
                    ) : (
                      "Place Order"
                    )}
                  </button>
                )}
              </div>
            </form>
          </div>
        </div>

        {/* Order Summary */}
        <div className="lg:col-span-1">
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 sticky top-6">
            <h2 className="text-lg font-bold mb-4">Order Summary</h2>

            <div className="max-h-60 overflow-y-auto mb-4">
              {items.map((item) => (
                <div key={item.id} className="flex py-2 border-b border-gray-100 last:border-0">
                  <div className="w-16 h-16 flex-shrink-0 mr-4">
                    <img
                      src={item.image || "/placeholder.svg"}
                      alt={item.name}
                      className="w-full h-full object-contain"
                    />
                  </div>
                  <div className="flex-1">
                    <p className="text-sm font-medium">{item.name}</p>
                    <p className="text-xs text-gray-500">Qty: {item.quantity}</p>
                    <p className="text-sm font-medium text-[#ff9906]">${(item.price * item.quantity).toFixed(2)}</p>
                  </div>
                </div>
              ))}
            </div>

            <div className="space-y-3 text-sm">
              <div className="flex justify-between">
                <span className="text-gray-600">Subtotal</span>
                <span className="font-medium">${totalAmount.toFixed(2)}</span>
              </div>

              <div className="flex justify-between">
                <span className="text-gray-600">Shipping</span>
                <span className="font-medium">{shippingCost === 0 ? "Free" : `$${shippingCost.toFixed(2)}`}</span>
              </div>

              <div className="flex justify-between">
                <span className="text-gray-600">Tax (8%)</span>
                <span className="font-medium">${taxAmount.toFixed(2)}</span>
              </div>

              <div className="border-t border-gray-200 pt-3 mt-3">
                <div className="flex justify-between font-bold">
                  <span>Total</span>
                  <span className="text-[#ff9906]">${orderTotal.toFixed(2)}</span>
                </div>
              </div>
            </div>

            {step === 4 && (
              <div className="mt-6 bg-green-50 border border-green-100 rounded-md p-3">
                <p className="text-sm text-green-800">
                  Your order is almost complete! Please review your information before placing your order.
                </p>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  )
}
